import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { getCropDetails } from '../../services/mycrops/myCropsService';
import LoadingComponent from '../loading/LoadingComponent';
// Balance Hídrico deshabilitado temporalmente para enfocar al usuario en Ciclos de Cultivo.
// import WaterBalance from './followup/WaterBalance';
import WaterConsumption from './followup/WaterConsumption';
import CycleList from './followup/CycleList';
import CycleDetailView from './followup/CycleDetailView';
import NewPlantingCycleModal from './NewPlantingCycleModal';
import SensorsUtils from '../utils/SensorsUtils';

const MyCropFollowUp = () => {
    const { cropId } = useParams();
    const [crop, setCrop] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedCycleId, setSelectedCycleId] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [cycleListRefreshKey, setCycleListRefreshKey] = useState(0);
    const [activeTab, setActiveTab] = useState('cycles'); // 'cycles' | 'water'

    const fetchDetails = useCallback(async () => {
        try {
            const data = await getCropDetails(cropId);
            if (data) {
                setCrop(data);
            } else {
                console.error("Crop not found");
            }
        } catch (error) {
            console.error("Error fetching crop details", error);
        } finally {
            setLoading(false);
        }
    }, [cropId]);

    useEffect(() => {
        fetchDetails();
    }, [fetchDetails]);

    if (loading) return <LoadingComponent />;
    if (!crop) return <div>Cultivo no encontrado.</div>;

    // El vendedor solo ve (no modifica) los cultivos de los alquileres y compras de sus clientes
    const readOnly = crop.editable === false;

    const renderContent = () => {
        if (activeTab === 'water') {
            return <WaterConsumption cropId={cropId} readOnly={readOnly} />;
        }
        if (selectedCycleId) {
            return (
                <CycleDetailView
                    cycleId={selectedCycleId}
                    onBack={() => setSelectedCycleId(null)}
                    readOnly={readOnly}
                />
            );
        }
        return (
            <CycleList
                key={`${cropId}-${cycleListRefreshKey}`}
                cropId={cropId}
                onSelectCycle={(id) => setSelectedCycleId(id)}
            />
        );
    };

    return (
        <div className="min-h-screen bg-gray-50 pb-2">
            {/* Header (no longer sticky — scrolls with the page to avoid overlapping the app's fixed header/mobile drawer) */}
            <div className="bg-white shadow-sm">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                        {/* Title Section */}
                        <div className="flex items-center">
                            <div className="min-w-0">
                                <h1 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2 truncate">
                                    {crop.name}
                                    <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${crop.active ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                                        {crop.active ? 'ACTIVO' : 'INACTIVO'}
                                    </span>
                                </h1>
                                <p className="text-xs md:text-sm text-gray-500 truncate">{crop.location}</p>
                            </div>
                        </div>

                        {/* Climate Dashboard Wrapper */}
                        <div className="flex flex-col sm:flex-row items-center gap-4 w-full lg:w-auto">
                            {/* Climate Summary Bar */}
                            <div className="flex items-center justify-around flex-1 bg-gray-50 px-3 md:px-5 py-2.5 rounded-xl border border-gray-100 shadow-inner w-full sm:w-auto">
                                <div className="flex flex-col items-center px-1 md:px-3">
                                    <span className="text-[9px] md:text-[10px] uppercase font-bold text-gray-400">Temp.</span>
                                    <div className="flex items-center text-gray-700">
                                        <span className="material-icons-outlined text-xs md:text-sm mr-1 text-yellow-500">thermostat</span>
                                        <span className="text-xs md:text-sm font-bold">{crop.lastTA ? `${crop.lastTA}°C` : '--'}</span>
                                    </div>
                                </div>
                                <div className="w-px h-6 md:h-8 bg-gray-200"></div>
                                <div className="flex flex-col items-center px-1 md:px-3">
                                    <span className="text-[9px] md:text-[10px] uppercase font-bold text-gray-400">Hum.</span>
                                    <div className="flex items-center text-gray-700">
                                        <span className="material-icons-outlined text-xs md:text-sm mr-1 text-blue-500">water_drop</span>
                                        <span className="text-xs md:text-sm font-bold">{crop.lastHA ? `${crop.lastHA}%` : '--'}</span>
                                    </div>
                                </div>
                                <div className="w-px h-6 md:h-8 bg-gray-200"></div>
                                <div className="flex flex-col items-center px-1 md:px-3">
                                    <span className="text-[9px] md:text-[10px] uppercase font-bold text-gray-400">Ilum.</span>
                                    <div className="flex items-center text-gray-700">
                                        <span className="material-icons-outlined text-xs md:text-sm mr-1 text-orange-400">light_mode</span>
                                        <span className="text-xs md:text-sm font-bold">{crop.lastRd !== undefined ? `${crop.lastRd}lx` : '--'}</span>
                                    </div>
                                </div>
                                <div className="w-px h-6 md:h-8 bg-gray-200 sm:hidden"></div>
                                <div className="sm:hidden ml-2">
                                    <div className={`text-[8px] uppercase font-bold px-2 py-1 rounded-full shadow-sm whitespace-nowrap ${SensorsUtils.getUpdateBadgeClass(crop.lastReadingDiffMinutes)}`}>
                                        {SensorsUtils.getUpdateLabel(crop.lastReadingDiffMinutes)}
                                    </div>
                                </div>
                            </div>

                            {/* Update Badge (Hidden on mobile inside the bar above, shown beside on tablet+) */}
                            <div className="hidden sm:block">
                                <div className={`text-[9px] uppercase font-bold px-3 py-1.5 rounded-full shadow-sm whitespace-nowrap ${SensorsUtils.getUpdateBadgeClass(crop.lastReadingDiffMinutes)}`}>
                                    {SensorsUtils.getUpdateLabel(crop.lastReadingDiffMinutes)}
                                </div>
                            </div>

                            {/* New Planting Button */}
                            {activeTab === 'cycles' && !selectedCycleId && !readOnly && (
                                <button
                                    onClick={() => setIsModalOpen(true)}
                                    className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white font-bold py-2.5 px-6 rounded-xl transition-all shadow-md flex items-center justify-center active:scale-95"
                                >
                                    <span className="material-icons mr-2">add</span>
                                    Nueva Siembra
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Navigation Tabs */}
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <div className="flex gap-6 border-b border-gray-100">
                        <button
                            onClick={() => setActiveTab('cycles')}
                            className={`py-3 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'cycles'
                                ? 'border-green-600 text-green-700'
                                : 'border-transparent text-gray-400 hover:text-gray-600'
                                }`}
                        >
                            Ciclos de Cultivo
                        </button>
                        <button
                            onClick={() => setActiveTab('water')}
                            className={`py-3 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'water'
                                ? 'border-blue-600 text-blue-700'
                                : 'border-transparent text-gray-400 hover:text-gray-600'
                                }`}
                        >
                            Consumo de Agua
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-8 animate-fade-in">
                {renderContent()}
            </div>

            <NewPlantingCycleModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                cropId={cropId}
                onCycleCreated={() => {
                    fetchDetails();
                    setCycleListRefreshKey((prev) => prev + 1);
                }}
            />
        </div>
    );
};

export default MyCropFollowUp;
